// src/pages/MiPerfilPage.jsx

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuthCliente } from '../context/AuthClienteContext';
import { auth, db } from '../firebase/firebase-config';
import { getStorage, ref as storageRef, uploadBytes, getDownloadURL } from "firebase/storage";
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import {
  Box,
  Heading,
  VStack,
  HStack,
  Text,
  Spinner,
  Center,
  Alert,
  AlertIcon,
  FormControl,
  FormLabel,
  Input,
  Button,
  useToast,
  Divider,
  Avatar,
  IconButton,
  Tag,
  Select
} from '@chakra-ui/react';
import { EditIcon } from '@chakra-ui/icons';

export default function MiPerfilPage() {
  const { currentUser, refreshCurrentUser } = useAuthCliente();
  const toast = useToast();
  const fileInputRef = useRef(null);

  // Preguntas dinámicas
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});

  // Nuevos campos
  const [sexo, setSexo] = useState('');
  const [birthDate, setBirthDate] = useState(''); // formato "YYYY-MM-DD"
  const [category, setCategory] = useState('');
  const [phone, setPhone] = useState('');
  const [loading, setLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);

  const [previewUrl, setPreviewUrl] = useState(null);

  const fetchProfileInfo = useCallback(async () => {
    if (!currentUser?.companyId || !currentUser?.uid) return;
    setLoading(true);
    try {
      // Carga preguntas de perfil de la compañía
      const companyRef = doc(db, 'companies', currentUser.companyId);
      const companySnap = await getDoc(companyRef);
      setQuestions(companySnap.exists() ? companySnap.data().profileQuestions || [] : []);

      // Carga datos del perfil del cliente
      const userProfileRef = doc(db, 'companies', currentUser.companyId, 'end_customers', currentUser.uid);
      const userProfileSnap = await getDoc(userProfileRef);
      if (userProfileSnap.exists()) {
        const data = userProfileSnap.data();
        setAnswers(data.profileAnswers || {});
        setSexo(data.sexo || '');
        setBirthDate(data.birthDate || '');
        setCategory(data.category || '');
        setPhone(data.phone || '');
      }
    } catch (err) {
      console.error(err);
      setError("No se pudo cargar tu perfil.");
    } finally {
      setLoading(false);
    }
  }, [currentUser?.companyId, currentUser?.uid]);

  useEffect(() => {
    fetchProfileInfo();
  }, [fetchProfileInfo]);

  const handleAnswerChange = (questionId, value) => {
    setAnswers(prev => ({ ...prev, [questionId]: value }));
  };

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    try {
      const userProfileRef = doc(db, 'companies', currentUser.companyId, 'end_customers', currentUser.uid);
      await updateDoc(userProfileRef, {
        sexo,
        birthDate: birthDate || null,
        category,
        profileAnswers: answers,
        phone,
      }, { merge: true });
      await refreshCurrentUser();
      toast({ title: "Perfil guardado", status: "success" });
    } catch (err) {
      console.error(err);
      toast({ title: "Error al guardar", status: "error" });
    } finally {
      setIsSaving(false);
    }
  };

  const handlePictureUpload = async (e) => {
    const file = e.target.files[0];
    if (!file || !auth.currentUser || !currentUser?.companyId) return;
    const localPreviewUrl = URL.createObjectURL(file);
    setPreviewUrl(localPreviewUrl);
    setIsSaving(true);
    const storage = getStorage();
    const picRef = storageRef(
      storage,
      `companies/${currentUser.companyId}/end_customers/${auth.currentUser.uid}/profilePicture/${file.name}`
    );
    try {
      const snapshot = await uploadBytes(picRef, file);
      const photoURL = await getDownloadURL(snapshot.ref);
      const userProfileRef = doc(db, 'companies', currentUser.companyId, 'end_customers', auth.currentUser.uid);
      await updateDoc(userProfileRef, { photoURL });
      await refreshCurrentUser();
      toast({ title: "Foto de perfil actualizada", status: "success" });
      setPreviewUrl(null);
    } catch (err) {
      console.error(err);
      toast({ title: "Error al subir la foto", status: "error" });
      setPreviewUrl(null);
    } finally {
      setIsSaving(false);
    }
  };

  if (loading) return <Center p={10}><Spinner size="xl" /></Center>;
  if (error) return <Alert status="error"><AlertIcon />{error}</Alert>;

  // Cálculo de edad si birthDate está presente
  let ageDisplay = null;
  if (birthDate) {
    const [y, m, d] = birthDate.split('-').map(Number);
    const dob = new Date(y, m - 1, d);
    const age = Math.floor((Date.now() - dob.getTime()) / 31557600000);
    ageDisplay = `${age} años`;
  }

  return (
    <Box p={{ base: 4, md: 8 }}>
      <VStack spacing={8} align="stretch" maxW="4xl" mx="auto">
        {/* Encabezado */}
        <HStack spacing={6} align="center">
          <Box position="relative">
            <Avatar
              size="2xl"
              name={currentUser?.name}
              src={previewUrl || currentUser?.photoURL}
            />
            <IconButton
              icon={<EditIcon />}
              size="sm"
              isRound
              colorScheme="red"
              position="absolute"
              bottom="0"
              right="0"
              aria-label="Cambiar foto"
              onClick={() => fileInputRef.current.click()}
              isLoading={isSaving}
            />
            <Input
              type="file"
              ref={fileInputRef}
              display="none"
              accept="image/*"
              onChange={handlePictureUpload}
            />
          </Box>
          <VStack align="start">
            <Heading>{currentUser?.name}</Heading>
            <Text color="gray.500">{currentUser?.email}</Text>
            {sexo && (
              <Tag colorScheme={sexo === 'femenino' ? 'pink' : 'blue'}>
                {sexo === 'femenino' ? '♀ Femenino' : '♂ Masculino'}
              </Tag>
            )}
            {ageDisplay && <Text fontSize="sm" color="gray.600">{ageDisplay}</Text>}
            {category && <Text fontSize="sm" color="gray.600">Categoría: {category}</Text>}
          </VStack>
        </HStack>

        <Divider />

        {/* Formulario de edición */}
        <Box as="form" onSubmit={handleSaveProfile} bg="white" p={6} borderRadius="xl" boxShadow="lg">
          <VStack spacing={6} align="stretch">
            <Heading size="md">Editar Perfil</Heading>

            <FormControl>
              <FormLabel>Sexo</FormLabel>
              <Select
                placeholder="Selecciona tu sexo"
                value={sexo}
                onChange={(e) => setSexo(e.target.value)}
              >
                <option value="masculino">Masculino</option>
                <option value="femenino">Femenino</option>
              </Select>
            </FormControl>

            <FormControl>
              <FormLabel>Fecha de nacimiento</FormLabel>
              <Input
                type="date"
                value={birthDate}
                onChange={(e) => setBirthDate(e.target.value)}
              />
            </FormControl>

            <FormControl id="phone" mb={4}>
            <FormLabel>Teléfono</FormLabel>
            <Input
            type="tel"
            placeholder="+52 1 55 1234 5678"
            value={phone}
            onChange={e => setPhone(e.target.value)}
            />
            </FormControl>

            <FormControl>
              <FormLabel>Categoría</FormLabel>
              <Input
                placeholder="Ej. Amateur, Profesional..."
                value={category}
                onChange={(e) => setCategory(e.target.value)}
              />
            </FormControl>

            <Divider />

            {questions.map(q => (
              <FormControl key={q.id}>
                <FormLabel>{q.text}</FormLabel>
                <Input
                  value={answers[q.id] || ''}
                  onChange={(e) => handleAnswerChange(q.id, e.target.value)}
                  placeholder="Tu respuesta..."
                />
              </FormControl>
            ))}

            <Button
              type="submit"
              colorScheme="green"
              isLoading={isSaving}
              alignSelf="flex-end"
            >
              Guardar Cambios
            </Button>
          </VStack>
        </Box>
      </VStack>
    </Box>
  );
}
